import { MenuButton } from './MenuButton';
import { HangupSettings } from './HangupSettings';
import { StatusIcon } from './StatusIcon';
import { useMemoizedFn } from 'ahooks';
import { OnlineStatus } from 'services/api/assistant.account';
import useNotice from 'hooks/useNotice';
import { useAuthorization } from 'components/Authorization';
import nimInst, { sendSystem } from 'services/nim';
import { Modal, message } from 'antd';
import { useTranslate } from '@tolgee/react';
import useRoles from 'hooks/business/useRoles';
import { HangupMenuButton } from './HangupMenuButton';

export interface UserStatusSwitchProps {
  popoverVisible: boolean;
  currentOnlineStatus: OnlineStatus;
  hidePopover: () => void;
  onCurrentOnlineStatus: (value: OnlineStatus) => void;
}

const statusActionMap = {
  ONLINE: 'RECOVER',
  HANGUP: 'HANG',
};

const updateStatus = (
  id: number,
  status: Exclude<Conversation.Status, 'OFFLINE'>,
  subStatus = '',
) => {
  return sendSystem({
    scene: 'p2p',
    to: 'leyan',
    sendToOnlineUsersOnly: false,
    cc: true,
    content: JSON.stringify({
      id,
      action: statusActionMap[status],
      subStatus,
    }),
  });
};

export const UserStatusSwitch = (props: UserStatusSwitchProps) => {
  const { currentOnlineStatus, popoverVisible, hidePopover, onCurrentOnlineStatus } = props;
  const { isSuperviser } = useRoles();

  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { t } = useTranslate();

  const id = payload?.id;
  const { status } = currentOnlineStatus;

  const { visible: onlineConfirmVisible, update: updateOnlineConfirm } = useNotice(
    'user/status/online',
    true,
  );

  // 切换为在线状态
  const handleOnline = useMemoizedFn(() => {
    hidePopover();

    if (status === 'ONLINE' || id === undefined) {
      return;
    }
    if (status === 'OFFLINE') {
      /**
       * 可能存在网络异常，尝试重新连接
       */
      nimInst.forceReconnect();
      return;
    }

    const onlineAction = () => {
      updateStatus(id, 'ONLINE')
        .then(() => {
          onCurrentOnlineStatus({
            status: 'ONLINE',
          });
          message.success(t('online.succeed'));
        })
        .catch(() => {
          message.error(t('online.failed'));
        });
    };

    if (!onlineConfirmVisible) {
      onlineAction();

      return;
    }

    Modal.confirm({
      title: t('confirm.title'),
      content: t('confirm.content'),
      onOk: () => {
        updateOnlineConfirm(false);
        onlineAction();
      },
      onCancel: () => {
        updateOnlineConfirm(false);
      },
    });
  });

  return (
    <>
      <div className="flex items-center justify-between mb-1">
        <span className="text-content-200 text-xs">{t('other.切换状态')}</span>
        {isSuperviser && <HangupSettings hidePopover={hidePopover} />}
      </div>
      <MenuButton
        key="ONLINE"
        label={t('status.online')}
        customIcon={<StatusIcon status="ONLINE" />}
        active={status === 'ONLINE'}
        onClick={handleOnline}
      />
      {!isSuperviser && (
        <HangupMenuButton
          popoverVisible={popoverVisible}
          currentOnlineStatus={currentOnlineStatus}
          hidePopover={hidePopover}
          onCurrentOnlineStatus={onCurrentOnlineStatus}
        />
      )}
    </>
  );
};
